import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import type { SidebarUser } from '../Sidebar/Sidebar';

/**
 * The account menu, placed top-right of the app (spec 41, 2 Oct). The user part
 * moved off the bottom-left of the sidebar; the location switcher stays there.
 *
 * It opens **downward** (anchored to the trigger's bottom) and right-aligns under
 * the trigger, since it lives in the top-right corner. The `bar` variant is a
 * horizontal pill — avatar · name / role · chevron — tuned for a light top bar,
 * so it uses the app ink tokens rather than the sidebar's. The menu body (Profile,
 * Settings, Theme, Log out) matches the old sidebar menu exactly.
 */

const MENU_GAP_PX = 8;
const MENU_MIN_WIDTH = 240;

interface MenuPosition {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
}

/** Downward, right-aligned under the trigger, clamped into the viewport. */
function computeMenuPosition(anchor: DOMRect): MenuPosition {
  const width = Math.max(anchor.width, MENU_MIN_WIDTH);
  const left = Math.min(
    Math.max(anchor.right - width, MENU_GAP_PX),
    window.innerWidth - width - MENU_GAP_PX,
  );
  const top = anchor.bottom + MENU_GAP_PX;
  const maxHeight = Math.max(window.innerHeight - top - MENU_GAP_PX, 120);
  return { left, top, width, maxHeight };
}

function MenuRow({
  icon,
  label,
  onClick,
  tone,
}: {
  icon: IconKind;
  label: string;
  onClick?: () => void;
  tone?: 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full cursor-pointer items-center gap-2.5 border-0 bg-transparent px-3 py-2.5 text-left font-[inherit] transition-colors',
        tone === 'danger'
          ? 'text-[var(--terracotta)] hover:bg-[var(--terracotta-tint)]'
          : 'text-[var(--ink-1)] hover:bg-[var(--cream-1)]',
      )}
    >
      <Icon kind={icon} size={15} stroke={1.8} color="currentColor" />
      <span className="text-[13px] font-medium">{label}</span>
    </button>
  );
}

export interface UserMenuProps {
  user: SidebarUser;
  /** Controlled open state; when omitted the menu manages its own. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** `bar` = horizontal pill for a light top bar (default). */
  variant?: 'bar';
  className?: string;
}

export function UserMenu({ user, open: openProp, onOpenChange, className }: UserMenuProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      onOpenChange?.(next);
      if (openProp === undefined) setInternalOpen(next);
    },
    [onOpenChange, openProp],
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    setMenuPosition(computeMenuPosition(trigger.getBoundingClientRect()));
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPosition(null);
      return;
    }
    updateMenuPosition();
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const onReposition = () => {
      updateMenuPosition();
    };
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    // Use click (not mousedown) so theme picker buttons receive their click first.
    document.addEventListener('click', onDoc);
    return () => {
      document.removeEventListener('click', onDoc);
    };
  }, [open, setOpen]);

  const initials = user.initials ?? user.name.slice(0, 2).toUpperCase();

  const portaledMenu =
    open && menuPosition
      ? createPortal(
          <div
            ref={menuRef}
            role="menu"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            style={{
              position: 'fixed',
              left: menuPosition.left,
              top: menuPosition.top,
              width: menuPosition.width,
              maxHeight: menuPosition.maxHeight,
              zIndex: 'var(--z-dropdown)',
            }}
            className="overflow-y-auto overflow-x-hidden rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-0)] shadow-[var(--sh-3)]"
          >
            <div className="flex items-center gap-2.5 border-b border-[var(--cream-2)] px-3 py-3">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[var(--gold)] to-[var(--ai-3)] text-[12px] font-bold text-white">
                {user.avatar ? (
                  <img src={user.avatar} alt="" className="h-full w-full rounded-full object-cover" />
                ) : (
                  initials
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-[var(--ink-1)]">{user.name}</span>
                {user.role && <span className="block truncate text-[11.5px] text-[var(--ink-3)]">{user.role}</span>}
              </span>
            </div>
            <div className="border-b border-[var(--cream-2)] py-1">
              <MenuRow icon="team" label="My profile" {...(user.onProfile ? { onClick: user.onProfile } : {})} />
              <MenuRow icon="cog" label="Settings" {...(user.onSettings ? { onClick: user.onSettings } : {})} />
            </div>
            <div className="py-1">
              <MenuRow icon="logout" label="Sign out" tone="danger" {...(user.onLogout ? { onClick: user.onLogout } : {})} />
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setOpen(!open);
        }}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Account: ${user.name}`}
        className="flex items-center gap-2.5 rounded-[var(--r-pill)] border border-[var(--cream-2)] bg-[var(--cream-0)] py-1 pl-1 pr-2.5 text-left font-[inherit] transition-colors hover:bg-[var(--cream-1)]"
      >
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[var(--gold)] to-[var(--ai-3)] text-[11px] font-bold text-white">
          {user.avatar ? (
            <img src={user.avatar} alt="" className="h-full w-full rounded-full object-cover" />
          ) : (
            initials
          )}
        </span>
        <span className="hidden min-w-0 leading-tight sm:block">
          <span className="block max-w-[140px] truncate text-[12.5px] font-semibold text-[var(--ink-1)]">{user.name}</span>
          {user.role && <span className="block max-w-[140px] truncate text-[11px] text-[var(--ink-3)]">{user.role}</span>}
        </span>
        <Icon kind="down" size={13} color="var(--ink-3)" />
      </button>
      {portaledMenu}
    </div>
  );
}
